// BCFClient — veřejné API integrační vrstvy (§26, §44). UI aplikace používá POUZE
// toto API. Client kompozičně spojuje services, adapter, resolver, workflow engine,
// repository, loader a writery. Nezná konkrétní IFC viewer (jen přes adapter).
import { createProject } from "../core/model.js";
import { BCFEventBus } from "../events/BCFEventBus.js";
import { IFCViewerAdapter } from "../adapters/IFCViewerAdapter.js";
import { ComponentResolver } from "../resolver/ComponentResolver.js";
import { MemoryBCFRepository } from "../repositories/BCFRepository.js";
import { WorkflowEngine } from "../workflow/WorkflowEngine.js";
import { TopicService } from "../services/TopicService.js";
import { CommentService } from "../services/CommentService.js";
import { ViewpointService } from "../services/ViewpointService.js";
import { WorkflowService } from "../services/WorkflowService.js";
import { BCFValidator } from "../validation/BCFValidator.js";
import { BCFLoader } from "../parser/BCFLoader.js";
import { writeBCFArchive } from "../writer/BCFWriter.js";

export const IMPORT_MODE = Object.freeze({ IMPORT_NEW: "IMPORT_NEW", MERGE: "MERGE", REPLACE: "REPLACE" });

export class BCFClient {
  constructor(options = {}) {
    this.project = createProject({ name: options.projectName || "BCF projekt" });
    this.events = options.events || new BCFEventBus();
    this.repository = options.repository || new MemoryBCFRepository();
    this.adapter = options.adapter || (options.viewer ? new IFCViewerAdapter(options.viewer) : null);
    this.resolver = this.adapter ? new ComponentResolver(this.adapter) : null;
    this.workflow = new WorkflowEngine(options.workflow);

    const currentUser = options.currentUser || (() => "user");
    const currentRole = options.currentRole || (() => "administrator");

    this.topics = new TopicService({ project: this.project, repository: this.repository, events: this.events, workflow: this.workflow, currentUser });
    this.comments = new CommentService({ topicService: this.topics, repository: this.repository, events: this.events, currentUser });
    this.viewpoints = new ViewpointService({ adapter: this.adapter, resolver: this.resolver, events: this.events });
    this.workflowService = new WorkflowService({ topicService: this.topics, engine: this.workflow, repository: this.repository, events: this.events, currentUser, currentRole });
    this.validator = new BCFValidator({ resolver: this.resolver });
    this._loader = new BCFLoader();
  }

  on(event, handler) { return this.events.on(event, handler); }
  off(event, handler) { this.events.off(event, handler); }

  // --- Import / export ------------------------------------------------------
  async load(file, { mode = IMPORT_MODE.MERGE } = {}) {
    const buffer = file instanceof ArrayBuffer || file instanceof Uint8Array ? file : await file.arrayBuffer();
    const { project, version, topicCount } = await this._loader.loadBCFBuffer(buffer);
    const conflicts = this._applyImport(project.topics, mode);
    for (const t of this.project.topics) await this.repository.saveTopic(t);
    this.events.emit("bcf.project.loaded", { version, topicCount, mode, conflicts });
    return { version, topicCount, conflicts };
  }

  // §31/§32 — import nepřepisuje slepě; MERGE dle GUID, konflikt dle ModifiedDate.
  _applyImport(incoming, mode) {
    const conflicts = [];
    if (mode === IMPORT_MODE.REPLACE) { this.project.topics = incoming.slice(); return conflicts; }
    const byId = new Map(this.project.topics.map((t) => [t.id, t]));
    for (const inc of incoming) {
      const existing = byId.get(inc.id);
      if (!existing) { this.project.topics.push(inc); byId.set(inc.id, inc); continue; }
      if (mode === IMPORT_MODE.IMPORT_NEW) continue;                 // ponech existující
      // MERGE — rozhodni dle ModifiedDate; nejednoznačné = konflikt (nepřepisovat).
      const a = Date.parse(existing.modifiedAt || existing.createdAt || 0);
      const b = Date.parse(inc.modifiedAt || inc.createdAt || 0);
      if (Number.isFinite(b) && Number.isFinite(a) && b > a) {
        Object.assign(existing, inc);
      } else if (!Number.isFinite(a) || !Number.isFinite(b) || a === b) {
        conflicts.push({ topicId: inc.id, reason: "unresolved" });
        existing._conflict = inc;                                    // ponecháno k ručnímu vyřešení
      }
    }
    return conflicts;
  }

  async export(version = "3.0") { return writeBCFArchive(this.project, version === "2.1" ? "2.1" : "3.0"); }
  async save() { for (const t of this.project.topics) await this.repository.saveTopic(t); return true; }

  // --- Témata ---------------------------------------------------------------
  createTopic(data) { return this.topics.createTopic(data); }
  getTopic(id) { return this.topics.getTopic(id); }
  listTopics() { return this.topics.listTopics(); }
  updateTopic(id, patch) { return this.topics.updateTopic(id, patch); }
  deleteTopic(id) { return this.topics.deleteTopic(id); }
  filterTopics(query) { return this.topics.filterTopics(query); }

  // --- Komentáře ------------------------------------------------------------
  addComment(topicId, comment) { return this.comments.addComment(topicId, comment); }
  listComments(topicId) { return this.comments.listComments(topicId); }

  // --- Viewpointy -----------------------------------------------------------
  async captureViewpoint(topicId = null, opts = {}) {
    if (!this.adapter) throw new Error("BCF: není připojen viewer/adapter.");
    const vp = await this.viewpoints.captureViewpoint(opts);
    if (topicId) {
      const topic = this.getTopic(topicId);
      if (!topic) throw new Error("Téma nenalezeno: " + topicId);
      topic.viewpoints.push(vp);
      await this.repository.saveTopic(topic);
    }
    return vp;
  }
  async applyViewpoint(viewpointId) {
    const vp = this._findViewpoint(viewpointId);
    if (!vp) throw new Error("Viewpoint nenalezen: " + viewpointId);
    return this.viewpoints.applyViewpoint(vp);
  }
  _findViewpoint(id) {
    for (const t of this.project.topics) { const v = t.viewpoints.find((x) => x.id === id); if (v) return v; }
    return null;
  }

  // --- Hlavní scénář: vytvoření tématu z aktuálního pohledu (§7, §34) --------
  async createIssueFromCurrentView(data = {}) {
    if (!this.adapter) throw new Error("BCF: není připojen viewer/adapter.");
    const vp = await this.viewpoints.captureViewpoint({ snapshot: data.snapshot !== false });
    const topic = await this.topics.createTopic(data);
    topic.viewpoints.push(vp);
    await this.repository.saveTopic(topic);
    this.events.emit("bcf.viewpoint.created", { viewpoint: vp, topicId: topic.id });
    return { topic, viewpoint: vp };
  }

  // --- Otevření tématu = obnovení pohledu (§16) -----------------------------
  async openTopic(topicId, { viewpointId = null } = {}) {
    const topic = this.getTopic(topicId);
    if (!topic) throw new Error("Téma nenalezeno: " + topicId);
    this.events.emit("bcf.topic.opened", { topicId });
    const vp = viewpointId ? topic.viewpoints.find((v) => v.id === viewpointId) : topic.viewpoints[0];
    if (!vp) return { topic, applied: null };
    const applied = await this.viewpoints.applyViewpoint(vp);
    return { topic, viewpoint: vp, applied };
  }

  // --- Workflow -------------------------------------------------------------
  changeStatus(topicId, status, ctx) { return this.workflowService.changeStatus(topicId, status, ctx); }
  assign(topicId, user, ctx) { return this.workflowService.assign(topicId, user, ctx); }
  allowedTransitions(topicId) { return this.workflowService.allowedTransitions(topicId); }

  // --- Validace -------------------------------------------------------------
  validate() { return this.validator.validateProject(this.project); }
}
